import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React from 'react';

const useStyles = makeStyles(() => ({
    container: {
        backgroundColor: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '12px',
        marginBottom: '8px',
        transition: 'border-color 0.2s',
        '&:hover': {
            borderColor: '#5eead4',
        },
    },
    folderIcon: {
        color: '#9ca3af',
    },
    folderName: {
        fontWeight: 500,
        color: '#374151',
    },
    mapping: {
        fontSize: '12px',
        color: '#6b7280',
        fontStyle: 'italic',
    },
    addSubfolderButton: {
        color: '#0d9488',
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '14px',
        '&:hover': {
            backgroundColor: '#f0fdfa',
        },
    },
    deleteButton: {
        color: '#dc2626',
        padding: '8px',
        '&:hover': {
            backgroundColor: '#fef2f2',
        },
    },
    chevronButton: {
        padding: '4px',
        '&:hover': {
            backgroundColor: '#f3f4f6',
        },
    },
}));

interface FolderItemRowProps {
    name: string;
    hint?: string;
    hasChildren: boolean;
    isExpanded: boolean;
    indentLevel: number;
    onToggleExpand: () => void;
    onAddSubfolder: () => void;
    onDelete?: () => void;
    isDeletable?: boolean;
}

export const FolderItemRow: React.FC<FolderItemRowProps> = ({
    name,
    hint,
    hasChildren,
    isExpanded,
    indentLevel,
    onToggleExpand,
    onAddSubfolder,
    onDelete,
    isDeletable = true,
}) => {
    const classes = useStyles();
    const indent = indentLevel * 24;

    return (
        <Box className={classes.container} style={{ marginLeft: `${indent}px` }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={1} flex={1}
                    onClick={onToggleExpand} sx={{ cursor: 'pointer' }}>
                    {hasChildren ? (
                        <IconButton
                            className={classes.chevronButton}
                            size="small"
                        >
                            {isExpanded ? (
                                <ExpandLessIcon fontSize="small" />
                            ) : (
                                <ExpandMoreIcon fontSize="small" />
                            )}
                        </IconButton>
                    ) : (
                        <Box width={24} />
                    )}
                    <FolderIcon className={classes.folderIcon} fontSize="small" />
                    <Typography className={classes.folderName}>
                        {name}
                        {hint && (
                            <Typography
                                component="span"
                                sx={{ fontStyle: 'italic', color: '#9ca3af', fontWeight: 400, ml: 0.5 }}
                            >
                                ({hint})
                            </Typography>
                        )}
                    </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Button
                        onClick={onAddSubfolder}
                        className={classes.addSubfolderButton}
                        size="small"
                    >
                        + Add Subfolder
                    </Button>
                    {isDeletable && onDelete && (
                        <IconButton
                            onClick={onDelete}
                            className={classes.deleteButton}
                            size="small"
                        >
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    )}
                </Stack>
            </Stack>
        </Box>
    );
};
